import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * App-wide error boundary. Without one, any render-time exception unmounts the whole React tree and
 * the user sees a blank screen (e.g. an intermittent crash on login). This catches the error, logs
 * it to the console for diagnosis, and shows a simple recoverable message with a reload button.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the real error visible to developers even though the UI is now a friendly fallback.
    console.error('[ErrorBoundary] Render crash:', error, info.componentStack);
  }

  handleReload = () => {
    // Clear the error and force a fresh load — usually resolves a transient data-timing crash.
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-dvh flex items-center justify-center p-6 bg-ui">
        <div className="max-w-sm w-full bg-ui-card rounded-2xl shadow-lg px-6 py-7 text-center shadow-black/40">
          <h1 className="text-h3 text-ui-text mb-1">Something went wrong</h1>
          <p className="text-sm text-ui-text2 mb-5">
            We hit a snag loading this screen. Reloading usually fixes it.
          </p>
          <button
            onClick={this.handleReload}
            className="w-full btn-sheen text-on-primary py-2.5 rounded-xl text-sm font-medium transition"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
